import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { DASH } from "@/constants/testIds";
import RequestChat from "@/components/RequestChat";
import { Folder, FileText, Upload, CheckCircle2, Clock, Eye, MessageCircle, Plus, DollarSign, ChevronRight } from "lucide-react";

const STATUS_MAP = {
  in_progress: { color: "#00d4ff", icon: Clock, key: "status_active" },
  review: { color: "#ff006e", icon: Eye, key: "status_review" },
  completed: { color: "#0a0a0a", icon: CheckCircle2, key: "status_completed" },
};

const REQUEST_STATUS_COLOR = {
  pending: "#8a8a8a",
  approved: "#00d4ff",
  in_progress: "#00d4ff",
  rejected: "#ff006e",
  completed: "#0a0a0a",
};

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("requests");
  const [projects, setProjects] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const [loadingR, setLoadingR] = useState(true);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/requests");
      setRequests(data);
      if (data.length && !selectedRequestId) setSelectedRequestId(data[0].request_id);
    } catch {
      setRequests([]);
    } finally {
      setLoadingR(false);
    }
  }, [selectedRequestId]);

  useEffect(() => {
    api.get("/dashboard/projects").then(({ data }) => setProjects(data)).catch(() => setProjects([])).finally(() => setLoadingP(false));
    loadRequests();
  }, [loadRequests]);

  const selectedRequest = requests.find((r) => r.request_id === selectedRequestId);

  return (
    <div className="min-h-screen bg-[#f5f5f5] pt-24 pb-20">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-2">
              · {user?.email}
            </div>
            <h1 className="font-mono font-bold tracking-tighter text-3xl sm:text-4xl text-[#0a0a0a]">
              {t.dashboard.title}
            </h1>
            <p className="mt-2 font-sans text-sm text-[#4a4a4a]">{t.dashboard.sub}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/nuevo-proyecto"
              data-testid="dash-new-project-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#0a0a0a] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors"
            >
              <Plus size={14} />
              {t.nav.new_project}
            </Link>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
              role / {user?.role}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-px bg-[#0a0a0a] border border-[#0a0a0a] w-fit mb-8">
          <TabBtn tid="dash-tab-requests" active={tab === "requests"} onClick={() => setTab("requests")} icon={MessageCircle}>
            Requests
          </TabBtn>
          <TabBtn tid={DASH.tabProjects} active={tab === "projects"} onClick={() => setTab("projects")} icon={Folder}>
            {t.dashboard.projects}
          </TabBtn>
          <TabBtn tid={DASH.tabFiles} active={tab === "files"} onClick={() => setTab("files")} icon={Upload}>
            {t.dashboard.files}
          </TabBtn>
        </div>

        {tab === "requests" && (
          <RequestsTab
            requests={requests}
            loading={loadingR}
            selectedRequestId={selectedRequestId}
            setSelectedRequestId={setSelectedRequestId}
            selectedRequest={selectedRequest}
            isAdmin={user?.role === "admin"}
            onReload={loadRequests}
          />
        )}

        {tab === "projects" && (
          <ProjectsTab loading={loadingP} projects={projects} t={t} />
        )}

        {tab === "files" && (
          <div className="bg-white border border-dashed border-[#0a0a0a] p-10 text-center">
            <Upload size={28} className="mx-auto text-[#0a0a0a] mb-4" />
            <p className="font-mono text-sm text-[#4a4a4a]">{t.dashboard.files_soon}</p>
          </div>
        )}
      </div>
    </div>
  );
}

const TabBtn = ({ tid, active, onClick, icon: Icon, children }) => (
  <button
    data-testid={tid}
    onClick={onClick}
    className={`flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
      active ? "bg-[#0a0a0a] text-white" : "bg-white text-[#0a0a0a] hover:bg-[#f5f5f5]"
    }`}
  >
    <Icon size={14} />
    {children}
  </button>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="uppercase tracking-[0.2em] text-[10px] text-[#8a8a8a]">{label}</span>
    <span className="text-[#0a0a0a]">{value || "—"}</span>
  </div>
);

function ProjectsTab({ loading, projects, t }) {
  if (loading) return <div className="font-mono text-xs text-[#8a8a8a]">…</div>;
  if (projects.length === 0)
    return (
      <div className="bg-white border border-[#0a0a0a] p-10 font-mono text-sm text-[#4a4a4a]">
        {t.dashboard.empty}
      </div>
    );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {projects.map((p, i) => {
        const s = STATUS_MAP[p.status] || STATUS_MAP.in_progress;
        const Icon = s.icon;
        return (
          <motion.div
            key={p.project_id}
            data-testid={DASH.projectCard(i)}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.06 }}
            className="bg-white border border-[#0a0a0a] p-6"
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-mono font-medium text-lg text-[#0a0a0a]">{p.title}</h3>
              <Icon size={16} style={{ color: s.color }} />
            </div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: s.color }}>
              {t.dashboard[s.key]}
            </div>
            <div className="mt-6 space-y-2 font-mono text-xs text-[#4a4a4a]">
              <Row label={t.dashboard.stage} value={p.stage} />
              <Row label={t.dashboard.deadline} value={p.deadline} />
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a] mb-2">
                <span>{t.dashboard.progress}</span>
                <span>{p.progress}%</span>
              </div>
              <div className="h-1 bg-[#eeeeee] relative">
                <div className="h-full transition-all" style={{ width: `${p.progress || 0}%`, background: s.color }} />
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function RequestsTab({ requests, loading, selectedRequestId, setSelectedRequestId, selectedRequest, isAdmin, onReload }) {
  if (loading) return <div className="font-mono text-xs text-[#8a8a8a]">…</div>;
  if (requests.length === 0) {
    return (
      <div className="bg-white border border-[#0a0a0a] p-10">
        <p className="font-sans text-sm text-[#4a4a4a]">
          No requests yet. Submit a new project to start the conversation.
        </p>
        <Link
          to="/nuevo-proyecto"
          data-testid="dash-empty-newproject"
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#0a0a0a] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors"
        >
          <Plus size={14} /> Start new project
        </Link>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <aside className="lg:col-span-4">
        <div className="border border-[#0a0a0a] bg-white">
          <div className="px-4 py-3 border-b border-[#0a0a0a] font-mono text-[10px] uppercase tracking-[0.25em] text-[#0a0a0a] bg-[#fafafa]">
            · requests ({requests.length})
          </div>
          <ul className="divide-y divide-[#e5e5e5]">
            {requests.map((r, i) => (
              <li key={r.request_id}>
                <button
                  data-testid={`dash-request-${i}`}
                  onClick={() => setSelectedRequestId(r.request_id)}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    selectedRequestId === r.request_id ? "bg-[#f5f5f5]" : "hover:bg-[#fafafa]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: REQUEST_STATUS_COLOR[r.status] || "#8a8a8a" }}>
                      {r.status}
                    </span>
                    <ChevronRight size={12} className="text-[#8a8a8a]" />
                  </div>
                  <div className="mt-1 font-mono font-medium text-sm text-[#0a0a0a] truncate">{r.title}</div>
                  {isAdmin && (
                    <div className="mt-0.5 font-mono text-[10px] text-[#8a8a8a] truncate">{r.client_email}</div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <section className="lg:col-span-8">
        {selectedRequest ? (
          <RequestDetail request={selectedRequest} isAdmin={isAdmin} onReload={onReload} />
        ) : (
          <div className="bg-white border border-[#0a0a0a] p-10 font-mono text-xs text-[#8a8a8a]">
            Select a request.
          </div>
        )}
      </section>
    </div>
  );
}

function RequestDetail({ request, isAdmin, onReload }) {
  const [showAnswers, setShowAnswers] = useState(false);
  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#0a0a0a] p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a]">
              {request.request_id}
            </div>
            <h2 className="mt-1 font-mono font-bold tracking-tight text-xl text-[#0a0a0a]">
              {request.title}
            </h2>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: REQUEST_STATUS_COLOR[request.status] || "#8a8a8a" }}>
              {request.status} · payment: {request.payment_status}
              {request.payment_amount ? ` · $${request.payment_amount} ${request.payment_currency}` : ""}
            </div>
          </div>
          <button
            onClick={() => setShowAnswers((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0a0a0a] hover:text-[#ff006e]"
          >
            {showAnswers ? "Hide brief" : "View brief"}
          </button>
        </div>

        {showAnswers && (
          <div className="mt-6 space-y-3 max-h-[300px] overflow-y-auto pr-2 border-t border-[#e5e5e5] pt-4">
            {Object.entries(request.answers || {}).map(([k, v]) => (
              <div key={k}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a]">{k}</div>
                <div className="mt-0.5 font-sans text-sm text-[#0a0a0a] whitespace-pre-wrap">{v || "—"}</div>
              </div>
            ))}
          </div>
        )}

        {isAdmin && <AdminControls request={request} onReload={onReload} />}
      </div>

      <RequestChat requestId={request.request_id} />
    </div>
  );
}

function AdminControls({ request, onReload }) {
  const [amount, setAmount] = useState(50);
  const [portion, setPortion] = useState("deposit");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const updateStatus = async (status) => {
    setBusy(true);
    setErr("");
    try {
      await api.patch(`/requests/${request.request_id}`, { status });
      await onReload();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setBusy(false);
    }
  };

  const issueOrder = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/requests/${request.request_id}/payment-order`, {
        amount: Number(amount),
        currency: "USD",
        portion,
      });
      await onReload();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-dashed border-[#e5e5e5]">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a] mb-3">
        · admin controls
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-px bg-[#0a0a0a] border border-[#0a0a0a]">
          {["approved", "in_progress", "rejected", "completed"].map((s) => (
            <button
              key={s}
              data-testid={`admin-status-${s}`}
              disabled={busy}
              onClick={() => updateStatus(s)}
              className="bg-white hover:bg-[#0a0a0a] hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3 p-4 bg-[#f5f5f5] border border-[#e5e5e5]">
        <DollarSign size={16} className="text-[#ff006e] mb-2" />
        <label className="block">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a] mb-1">Amount (USD)</div>
          <input
            data-testid="admin-payment-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-28 px-3 py-2 border border-[#0a0a0a] bg-white font-sans text-sm"
          />
        </label>
        <label className="block">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a] mb-1">Portion</div>
          <select
            data-testid="admin-payment-portion"
            value={portion}
            onChange={(e) => setPortion(e.target.value)}
            className="px-3 py-2 border border-[#0a0a0a] bg-white font-sans text-sm"
          >
            <option value="deposit">Deposit (50%)</option>
            <option value="balance">Balance (50%)</option>
            <option value="full">Full (100%)</option>
          </select>
        </label>
        <button
          onClick={issueOrder}
          disabled={busy || !amount}
          data-testid="admin-issue-order"
          className="px-5 py-2 bg-[#0a0a0a] text-white font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors disabled:opacity-50"
        >
          Issue payment order
        </button>
      </div>

      {err && <div className="mt-3 font-mono text-xs text-[#ff006e]">{err}</div>}
    </div>
  );
}
